import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SecondaryBlueButton } from "@velaro/velaro-component-lib";
import LoadingSpinner from "@/components/LoadingSpinner";
import useApi from "@/hooks/useApi";

const CB_COLOR = "#C62828";

type Step = 1 | 2 | 3;

interface TestResult {
  valid: boolean;
  propertyName?: string;
  propertyType?: string;
  error?: string;
}

export default function CloudbedsCreate() {
  const navigate = useNavigate();
  const api = useApi();

  const [step, setStep] = useState<Step>(1);
  const [apiKey, setApiKey] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [pushTranscripts, setPushTranscripts] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleTest() {
    setError(null);
    setTestResult(null);
    if (!apiKey.trim()) { setError("Enter your Cloudbeds API key."); return; }
    if (!propertyId.trim()) { setError("Enter your Cloudbeds Property ID."); return; }
    setTesting(true);
    try {
      const resp = await api.admin.post("Cloudbeds/Test", { apiKey: apiKey.trim(), propertyId: propertyId.trim() });
      const data = await resp.json() as TestResult;
      setTestResult(data);
      if (data.valid) {
        if (!displayName) setDisplayName(data.propertyName || "Cloudbeds");
        setStep(2);
      } else {
        setError(data.error || "Connection failed. Check your API key and Property ID.");
      }
    } catch {
      setError("Failed to reach Cloudbeds. Please try again.");
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const resp = await api.admin.post("Cloudbeds/Configs", {
        displayName: displayName.trim() || "Cloudbeds",
        apiKey: apiKey.trim(),
        propertyId: propertyId.trim(),
        pushTranscripts,
        propertyName: testResult?.propertyName ?? ""
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({})) as { error?: string };
        setError(body?.error ?? "Failed to save. Please try again.");
        return;
      }
      setStep(3);
    } catch {
      setError("An unexpected error occurred.");
    } finally {
      setSaving(false);
    }
  }

  const steps = [{ num: 1, label: "Connect API" }, { num: 2, label: "Configure" }, { num: 3, label: "Done" }];

  return (
    <div className="max-w-2xl mx-auto py-6">
      <div className="flex items-center gap-2 mb-8">
        {steps.map((s, i) => (
          <React.Fragment key={s.num}>
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= s.num ? "text-white" : "bg-slate-200 text-slate-500"}`}
                style={step >= s.num ? { backgroundColor: CB_COLOR } : {}}>
                {step > s.num ? "✓" : s.num}
              </div>
              <span className={`text-sm ${step === s.num ? "font-semibold text-slate-800" : "text-slate-400"}`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && <div className="flex-1 h-px bg-slate-200" />}
          </React.Fragment>
        ))}
      </div>

      {step === 1 && (
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="font-semibold text-slate-800 text-lg mb-1">Connect Cloudbeds</div>
          <p className="text-sm text-slate-500 mb-6">Enter your Cloudbeds API key and Property ID. All reservations, room types, availability, and guest folios become instantly AI-searchable.</p>

          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 mb-5 text-xs text-slate-600">
            <div className="font-semibold text-slate-700 mb-2">Finding your credentials</div>
            <div className="space-y-3">
              <div>
                <div className="font-medium text-slate-700 mb-1">API Key</div>
                <ol className="space-y-1 list-decimal list-inside">
                  <li>Log in to <strong>hotels.cloudbeds.com</strong></li>
                  <li>Go to <strong>Apps &amp; Integrations → API</strong></li>
                  <li>Click <strong>Generate API Key</strong> → select scopes: reservations, guests, rooms, housekeeping</li>
                </ol>
              </div>
              <div>
                <div className="font-medium text-slate-700 mb-1">Property ID</div>
                <ol className="space-y-1 list-decimal list-inside">
                  <li>In Cloudbeds dashboard, look at your browser URL</li>
                  <li>The number after <code className="bg-white px-1 rounded border border-slate-200">/hotels/</code> is your Property ID</li>
                  <li>Example: <code className="bg-white px-1 rounded border border-slate-200">hotels.cloudbeds.com/hotels/12345</code> → Property ID is <strong>12345</strong></li>
                </ol>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">API Key <span className="text-red-500">*</span></label>
              <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                placeholder="cb_..."
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoComplete="off" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Property ID <span className="text-red-500">*</span></label>
              <input type="text" value={propertyId} onChange={(e) => setPropertyId(e.target.value)}
                placeholder="12345"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <p className="text-xs text-slate-400 mt-1">Numeric ID from your Cloudbeds dashboard URL. Connect one per Velaro site, or add multiple for hotel groups.</p>
            </div>
          </div>

          {error && <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}

          <div className="flex gap-3 mt-6">
            <button type="button" onClick={handleTest} disabled={testing}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-2"
              style={{ backgroundColor: CB_COLOR }}>
              {testing ? <><LoadingSpinner /> Testing...</> : "Test & Connect →"}
            </button>
            <SecondaryBlueButton label="Cancel" onClick={() => navigate("/integrations/cloudbeds")} />
          </div>
        </div>
      )}

      {step === 2 && testResult?.valid && (
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="rounded-lg bg-green-50 border border-green-200 p-4 mb-5 flex items-center gap-3">
            <svg viewBox="0 0 20 20" width="20" height="20" fill="#16a34a"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
            <div>
              <div className="font-semibold text-green-800">Connected successfully!</div>
              {testResult.propertyName && <div className="text-sm text-green-700">Property: {testResult.propertyName}</div>}
              {testResult.propertyType && <div className="text-sm text-green-700">Type: {testResult.propertyType}</div>}
            </div>
          </div>

          <div className="font-semibold text-slate-800 text-lg mb-1">Configure connection</div>
          <p className="text-sm text-slate-500 mb-5">Give this property connection a name and configure transcript push behavior.</p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Display Name</label>
              <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                placeholder="My Hotel"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="flex items-start gap-3">
              <input type="checkbox" id="pushTranscripts" checked={pushTranscripts} onChange={(e) => setPushTranscripts(e.target.checked)} className="mt-1 rounded border-slate-300" />
              <div>
                <label htmlFor="pushTranscripts" className="text-sm font-medium text-slate-700">Push transcripts to Cloudbeds reservation notes</label>
                <p className="text-xs text-slate-400 mt-0.5">Attach the chat transcript to the guest's reservation record in Cloudbeds after each conversation resolves.</p>
              </div>
            </div>
          </div>

          {error && <div className="mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}

          <div className="flex gap-3 mt-6">
            <button type="button" onClick={handleSave} disabled={saving}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white flex items-center gap-2"
              style={{ backgroundColor: CB_COLOR }}>
              {saving ? <><LoadingSpinner /> Saving...</> : "Save Connection →"}
            </button>
            <SecondaryBlueButton label="Back" onClick={() => setStep(1)} />
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="bg-white rounded-2xl shadow-md p-6 text-center">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: CB_COLOR }}>
            <svg viewBox="0 0 24 24" width="32" height="32" fill="white"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" /></svg>
          </div>
          <div className="text-xl font-semibold text-slate-800 mb-2">Cloudbeds is connected!</div>
          <p className="text-sm text-slate-500 mb-6">All 20 AI tools are now active. Build a Pre-Arrival Bot, In-Stay Concierge, or Booking Bot using the Starter Wizard.</p>
          <div className="flex gap-3 justify-center">
            <button type="button" onClick={() => navigate("/integrations/cloudbeds")}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white" style={{ backgroundColor: CB_COLOR }}>
              Go to Cloudbeds →
            </button>
            <SecondaryBlueButton label="Back to Integrations" onClick={() => navigate("/integrations")} />
          </div>
        </div>
      )}
    </div>
  );
}
